import { useEffect, useRef, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { useSession } from "../../auth/session"
import Api from "../../api/api"
import { Subscription, SubscriptionStatus } from "../../api/models"
import { useSku } from "../../redux/skusSlice"
import Spinner from "../widgets/Spinner"

// same pills as the customer subscription page's status banner
const statusPills: Record<SubscriptionStatus, { label: string, className: string }> = {
  on_trial: { label: "Trial", className: "status-on-trial" },
  active: { label: "Active", className: "status-active" },
  paused: { label: "Paused", className: "status-paused" },
  past_due: { label: "Past due", className: "status-past-due" },
  unpaid: { label: "Unpaid", className: "status-unpaid" },
  cancelled: { label: "Cancelled", className: "status-cancelled" },
  expired: { label: "Expired", className: "status-expired" },
}

function formatDate(d?: string | null) {
  if (!d) return null
  return new Date(d).toLocaleDateString()
}

// "SKU name vX.Y.Z" with the billed offering's price/interval underneath,
// both resolved from the cached skus slice
function SkuDetail({ subscription }: { subscription: Subscription }) {
  const { data: sku } = useSku(subscription.skuId)
  const offering = subscription.offeringId
    ? sku?.offerings.find((offering) => offering.id === subscription.offeringId)
    : undefined
  return (
    <>
      <div className="detail-item">
        <div className="detail-label">SKU</div>
        <div className="detail-value" title={subscription.skuId}>
          <Link to={`/skus/${subscription.skuId}`} className="card-link">{sku?.name ?? subscription.skuId}</Link>
          {" "}v{subscription.skuVersion}
        </div>
      </div>
      {offering && offering.intervalUnit && (
        <div className="detail-item">
          <div className="detail-label">Billing</div>
          <div className="detail-value">
            ${(offering.priceCents / 100).toFixed(2)} / {offering.intervalCount != null && offering.intervalCount > 1 ? `${offering.intervalCount} ${offering.intervalUnit}s` : offering.intervalUnit}
          </div>
        </div>
      )}
    </>
  )
}

// Basic admin/dev subscription detail page — the target of the subscription
// origin links in the entitlements and licenses tables. Admin-only surface:
// there is no /me route for viewing a subscription by id (customers have
// /subscription), and developers only reach subscriptions of their own skus
// (server-side 403).
export default function SubscriptionDetail() {
  const { id } = useParams<{ id: string }>()
  const session = useSession()
  const accessToken = session.accessToken ?? ""

  const [subscription, setSubscription] = useState<Subscription | undefined>(undefined)
  const [error, setError] = useState(false)
  // stale responses (id changed mid-flight) must not land in state
  const requestSeq = useRef(0)

  useEffect(() => {
    const seq = ++requestSeq.current
    setSubscription(undefined)
    setError(false)
    Api.adminGetSubscription({ subscriptionId: id!, accessToken })
      .then(({ subscription }) => {
        if (seq === requestSeq.current) setSubscription(subscription)
      })
      .catch(() => {
        if (seq === requestSeq.current) setError(true)
      })
  }, [id, accessToken])

  if (error) {
    return (
      <div className="page">
        <p className="empty-state">Error loading subscription</p>
      </div>
    )
  }

  if (!subscription) {
    return (
      <div className="page">
        <p className="empty-state"><Spinner size={16} /> Loading subscription…</p>
      </div>
    )
  }

  const pill = statusPills[subscription.status]

  return (
    <div className="page">
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12 }}>
        <h1 className="page-title" style={{ margin: 0 }}>Subscription</h1>
        {pill && (
          <span className={`status-badge ${pill.className}`} style={{ flexShrink: 0 }}>{pill.label}</span>
        )}
      </div>

      <div className="detail-grid" style={{ marginTop: 24 }}>
        <div className="detail-item">
          <div className="detail-label">User</div>
          <div className="detail-value" style={{ wordBreak: "break-all" }}>
            <Link to={`/users/${subscription.userId}`} className="card-link">{subscription.userId}</Link>
          </div>
        </div>
        <SkuDetail subscription={subscription} />
        <div className="detail-item">
          <div className="detail-label">Renews</div>
          <div className="detail-value">{formatDate(subscription.renewsAt) ?? "—"}</div>
        </div>
        <div className="detail-item">
          <div className="detail-label">Ends</div>
          <div className="detail-value">{formatDate(subscription.endsAt) ?? "—"}</div>
        </div>
        <div className="detail-item">
          <div className="detail-label">Store subscription</div>
          <div className="detail-value">
            <a href={subscription.storeSubscriptionUrl} target="_blank" rel="noreferrer" className="card-link">
              #{subscription.storeSubscriptionId} ↗
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
